'use es6';

import Raven from 'raven-js';
const privy = new WeakMap();
/**
 * @typedef {Object} LocalStorageClient~Options
 * @property {Function} parse - Function to parse stored items, default is `JSON.parse`
 * @property {Function} stringify - Function to serialize stored items, default is `JSON.stringify`
 */

/**
 * Creates a localStorage client
 */

export default class LocalStorageClient {
  /**
   * @param {String} key
   * @param {LocalStorageClient~Options} options
   * @constructor
   */
  constructor(key, options) {
    options = options || {};
    const parse = options.parse || JSON.parse;
    const stringify = options.stringify || JSON.stringify;
    privy.set(this, {
      key,
      parse,
      stringify
    });
  }

  get key() {
    return privy.get(this).key;
  }

  get parse() {
    return data => {
      try {
        /* https://git.hubteam.com/HubSpot/DesignManagerUI/issues/4795 */
        return JSON.parse(data);
      } catch (error) {
        Raven.captureException(error, {
          extra: {
            data
          }
        });
        return null;
      }
    };
  }

  get stringify() {
    return privy.get(this).stringify;
  }
  /**
   * Retrieves an item from localStorage
   * @param {Object} options
   * @returns {*}
   */


  fetch(options) {
    const {
      onError
    } = options || {};
    let data;

    try {
      data = localStorage.getItem(this.key);
      data = typeof data === 'string' ? this.parse(data) : null;
    } catch (error) {
      data = null;

      if (typeof onError === 'function') {
        onError(error);
      }
    }

    return data;
  }
  /**
   * Retrieves an item from localStorage
   * @returns {Promise}
   */


  fetchAsync() {
    return new Promise((resolve, reject) => {
      const result = this.fetch({
        onError: reject
      });
      resolve(result);
    });
  }
  /**
   * Stores `data` to localStorage
   * @param {*} data
   * @param {Object} options
   * @returns {*}
   */


  save(data, options) {
    const {
      onError
    } = options || {};

    try {
      const serialized = this.stringify(data);

      if (serialized) {
        localStorage.setItem(this.key, serialized);
      }
    } catch (error) {
      if (typeof onError === 'function') {
        onError(error);
      }
    }

    return data;
  }
  /**
   * Stores `data` to localStorage
   * @param {*} data
   * @returns {Promise}
   */


  saveAsync(data) {
    return new Promise((resolve, reject) => {
      const result = this.save(data, {
        onError: reject
      });
      resolve(result);
    });
  }
  /**
   * Deletes the item from localStorage.
   * @param {Object} options
   * @returns {Boolean}
   */


  del(options) {
    let success;
    const {
      onError
    } = options || {};

    try {
      localStorage.removeItem(this.key);
      success = true;
    } catch (error) {
      success = false;

      if (typeof onError === 'function') {
        onError(error);
      }
    }

    return success;
  }
  /**
   * Deletes the item from localStorage.
   * @returns {Promise}
   */


  delAsync() {
    return new Promise((resolve, reject) => {
      const result = this.del({
        onError: reject
      });
      resolve(result);
    });
  }

}